import React, { useState, useEffect } from 'react';
import { Property } from '../types/Property';
import './BookingSystem.css';
import { DEFAULT_RECEIVER_EVM } from '../config/hedera';
import { getEthereumProvider, ensureHederaTestnet, sendHBAR } from '../wallet/metamask';

export interface Booking {
  id: string;
  propertyId: string;
  propertyName: string;
  guestEmail: string;
  guestName: string;
  guestWallet: string;
  hostEmail: string;
  hostWallet: string;
  startDate: string;
  endDate: string;
  totalPrice: number;
  status: 'pending' | 'confirmed' | 'cancelled';
  createdAt: string;
  contactDetails?: {
    phone?: string;
    email?: string;
    address?: string;
  };
}

interface BookingSystemProps {
  property: Property;
  hostEmail: string;
  hostWallet: string;
  contactDetails?: {
    phone?: string;
    email?: string;
    address?: string;
  };
  onBookingCreated?: (booking: Booking) => void;
}

const BookingSystem: React.FC<BookingSystemProps> = ({
  property,
  hostEmail,
  hostWallet,
  contactDetails,
  onBookingCreated
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [bookedDates, setBookedDates] = useState<string[]>([]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);

  // Load existing bookings for this property - reload when bookings change
  const loadBookedAndBlockedDates = () => {
    // Check BOTH bookings and booking NFTs
    const bookings = JSON.parse(localStorage.getItem('ethereus_bookings') || '[]');
    const bookingNFTs = JSON.parse(localStorage.getItem('ethereus_booking_nfts') || '[]');
    
    // Get all bookings for this property from both sources
    const propertyBookings = bookings.filter((booking: Booking) => 
      String(booking.propertyId).trim() === String(property.id).trim() && booking.status !== 'cancelled'
    );
    
    // Also check booking NFTs
    const propertyBookingNFTs = bookingNFTs.filter((nft: any) => 
      String(nft.propertyId).trim() === String(property.id).trim()
    );
    
    const dates: string[] = [];
    
    // Add dates from bookings
    propertyBookings.forEach((booking: Booking) => {
      const start = new Date(booking.startDate);
      const end = new Date(booking.endDate);
      const current = new Date(start);
      
      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];
        if (!dates.includes(dateStr)) dates.push(dateStr);
        current.setDate(current.getDate() + 1);
      }
    });
    
    // Add dates from booking NFTs
    propertyBookingNFTs.forEach((nft: any) => {
      if (nft.startDate && nft.endDate) {
        const start = new Date(nft.startDate);
        const end = new Date(nft.endDate);
        const current = new Date(start);
        
        while (current <= end) {
          const dateStr = current.toISOString().split('T')[0];
          if (!dates.includes(dateStr)) dates.push(dateStr);
          current.setDate(current.getDate() + 1);
        }
      }
    });
    
    setBookedDates(dates);
    
    // Load host-blocked dates
    const blocked = JSON.parse(localStorage.getItem(`afg_blocked_${property.id}`) || '[]');
    setBlockedDates(blocked);
  };
  
  useEffect(() => {
    loadBookedAndBlockedDates();
    
    // Listen for booking updates
    const handleBookingUpdate = () => {
      loadBookedAndBlockedDates();
    };
    window.addEventListener('afg:bookings-updated', handleBookingUpdate);
    
    // Listen for booking updates across tabs
    let bookingChannel: any;
    try {
      bookingChannel = new BroadcastChannel('afg-bookings');
      bookingChannel.onmessage = (ev: any) => {
        if (ev.data && (ev.data.type === 'booking-created' || ev.data.type === 'booking-updated')) {
          loadBookedAndBlockedDates();
        }
      };
    } catch {}
    
    // Listen for storage changes (blocked dates might be updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'ethereus_bookings' || e.key === 'ethereus_booking_nfts' || 
          (e.key && e.key.startsWith('afg_blocked_'))) {
        loadBookedAndBlockedDates();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('afg:bookings-updated', handleBookingUpdate);
      window.removeEventListener('storage', handleStorageChange);
      try { bookingChannel && bookingChannel.close && bookingChannel.close(); } catch {}
    };
  }, [property.id]);

  const calculateTotalPrice = () => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const nights = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    return nights * property.price;
  };

  const isDateBooked = (date: string) => {
    return bookedDates.includes(date) || blockedDates.includes(date);
  };

  const handleBooking = async () => {
    if (!startDate || !endDate || !guestEmail || !guestName) {
      alert('Please fill in all required fields');
      return;
    }
    
    // CRITICAL: Check if ANY date in the selected range is booked or blocked
    const start = new Date(startDate);
    const end = new Date(endDate);
    const cur = new Date(start);
    const unavailableDates: string[] = [];
    
    while (cur <= end) {
      const d = cur.toISOString().split('T')[0];
      if (bookedDates.includes(d)) {
        unavailableDates.push(d);
      }
      if (blockedDates.includes(d)) {
        unavailableDates.push(d);
      }
      cur.setDate(cur.getDate() + 1);
    }
    
    if (unavailableDates.length > 0) {
      const dateList = unavailableDates.slice(0, 5).map(d => new Date(d).toLocaleDateString()).join(', ');
      const more = unavailableDates.length > 5 ? ` and ${unavailableDates.length - 5} more` : '';
      alert(`Selected dates include unavailable dates (booked or blocked by host): ${dateList}${more}. Please choose different dates.`);
      setIsBooking(false);
      return;
    }

    const provider = await getEthereumProvider();
    if (!provider) { alert('MetaMask is not installed.'); return; }

    setIsBooking(true);

    try {
      await ensureHederaTestnet(provider);
      const accounts = await provider.request({ method: 'eth_requestAccounts' });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. Please connect MetaMask.');
      }

      const account = accounts[0];
      console.log('Connected account:', account);

      // Check if user has enough HBAR balance
      const balance = await provider.request({
        method: 'eth_getBalance',
        params: [account, 'latest']
      });

      const balanceInHBAR = parseInt(balance, 16) / 1e18;
      const totalPrice = calculateTotalPrice();
      
      if (balanceInHBAR < totalPrice) {
        alert(`Insufficient HBAR balance. You have ${balanceInHBAR.toFixed(2)} HBAR but need ${totalPrice} HBAR.`);
        setIsBooking(false);
        return;
      }

      const dataHex = `0x${property.id.replace(/[^a-fA-F0-9]/g, '').padStart(64, '0')}`;
      const txHash = await sendHBAR(provider, DEFAULT_RECEIVER_EVM, totalPrice, dataHex);

      console.log('Transaction hash:', txHash);

      // Wait for transaction confirmation
      let receipt = null;
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds timeout

      while (!receipt && attempts < maxAttempts) {
        try {
          receipt = await provider.request({
            method: 'eth_getTransactionReceipt',
            params: [txHash]
          });
        } catch (error) {
          console.log('Waiting for transaction confirmation...');
        }
        
        if (!receipt) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
          attempts++;
        }
      }

      if (receipt && receipt.status === '0x1') {
        // Transaction successful - create booking
        const booking: Booking = {
          id: `booking_${Date.now()}`,
          propertyId: property.id,
          propertyName: property.name,
          guestEmail,
          guestName,
          guestWallet: account,
          hostEmail,
          hostWallet,
          startDate,
          endDate,
          totalPrice,
          status: 'confirmed',
          createdAt: new Date().toISOString(),
          contactDetails
        };

        // Save booking to localStorage
        const existingBookings = JSON.parse(localStorage.getItem('ethereus_bookings') || '[]');
        existingBookings.push(booking);
        localStorage.setItem('ethereus_bookings', JSON.stringify(existingBookings));
        
        // Dispatch event to notify host dashboard of new booking
        try {
          window.dispatchEvent(new Event('afg:bookings-updated'));
          // Also broadcast across tabs
          const bc = new BroadcastChannel('afg-bookings');
          bc.postMessage({ type: 'booking-created', bookingId: booking.id, propertyId: booking.propertyId });
          setTimeout(() => bc.close(), 100);
        } catch {}

        // Create Booking NFT
        const bookingNFT = {
          id: `booking_nft_${Date.now()}`,
          bookingId: booking.id,
          propertyId: property.id,
          propertyName: property.name,
          tokenId: Date.now().toString(),
          contractAddress: '0.0.7054981',
          ownerAddress: account,
          purchasePrice: totalPrice,
          purchaseDate: new Date().toISOString(),
          startDate: booking.startDate,
          endDate: booking.endDate,
          totalPrice: totalPrice,
          location: property.location,
          transactionHash: txHash,
          imageUrl: property.imageUrl,
          metadata: {
            rarity: 'common',
            benefits: [
              'Digital booking confirmation',
              'Access to property updates',
              'Exclusive guest perks',
              'Loyalty points accumulation'
            ],
            votingPower: 1
          }
        };

        const existingNFTs = JSON.parse(localStorage.getItem('ethereus_booking_nfts') || '[]');
        existingNFTs.push(bookingNFT);
        localStorage.setItem('ethereus_booking_nfts', JSON.stringify(existingNFTs));

        // Update booked dates
        const dates: string[] = [];
        const bookingStart = new Date(startDate);
        const bookingEnd = new Date(endDate);
        const current = new Date(bookingStart);
        
        while (current <= bookingEnd) {
          dates.push(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + 1);
        }
        
        // Reload booked dates after successful booking
        loadBookedAndBlockedDates();

        if (onBookingCreated) {
          onBookingCreated(booking);
        }

        alert(`Booking confirmed! You've paid ${totalPrice} HBAR for ${property.name}\nTransaction Hash: ${txHash}`);
        
        // Reset form
        setStartDate('');
        setEndDate('');
        setGuestEmail('');
        setGuestName('');
      } else {
        throw new Error('Transaction failed or timed out');
      }
    } catch (error: any) {
      console.error('Booking failed:', error);
      alert(`Booking failed: ${error.message}`);
    } finally {
      setIsBooking(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getMinDate = () => {
    return formatDate(new Date());
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    return formatDate(maxDate);
  };

  return (
    <div className="booking-system">
      <h3>Book This Property</h3>
      
      <div className="booking-form">
        <div className="form-row">
          <div className="form-group">
            <label>Check-in Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                if (isDateBooked(selectedDate)) {
                  alert('This date is unavailable (booked or blocked). Please select a different date.');
                  return;
                }
                setStartDate(selectedDate);
                // If new start date is after end date, clear end date
                if (endDate && selectedDate > endDate) {
                  setEndDate('');
                }
              }}
              onBlur={(e) => {
                if (e.target.value && isDateBooked(e.target.value)) {
                  setStartDate('');
                }
              }}
              min={getMinDate()}
              max={getMaxDate()}
              className={isDateBooked(startDate) ? 'booked-date' : ''}
              style={isDateBooked(startDate) ? { backgroundColor: '#e2e8f0', color: '#2d3748', cursor: 'not-allowed', fontWeight: '600', opacity: 0.7, textDecoration: 'line-through' } : {}}
            />
            {isDateBooked(startDate) && (
              <span className="date-warning" style={{ fontSize: '13px', color: '#2d3748', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                <span style={{ fontSize: '16px' }}>🚫</span>
                <span style={{ textDecoration: 'line-through' }}>This date is unavailable (booked or blocked)</span>
              </span>
            )}
          </div>
          
          <div className="form-group">
            <label>Check-out Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                if (isDateBooked(selectedDate)) {
                  alert('This date is unavailable (booked or blocked). Please select a different date.');
                  return;
                }
                // Also check if any date in the range from startDate to selectedDate is unavailable
                if (startDate) {
                  const start = new Date(startDate);
                  const end = new Date(selectedDate);
                  const cur = new Date(start);
                  const unavailable: string[] = [];
                  
                  while (cur <= end) {
                    const d = cur.toISOString().split('T')[0];
                    if (isDateBooked(d)) {
                      unavailable.push(d);
                    }
                    cur.setDate(cur.getDate() + 1);
                  }
                  
                  if (unavailable.length > 0) {
                    const dateList = unavailable.slice(0, 3).map(d => new Date(d).toLocaleDateString()).join(', ');
                    alert(`Some dates in your selected range are unavailable (booked or blocked): ${dateList}${unavailable.length > 3 ? '...' : ''}. Please choose a different date range.`);
                    return;
                  }
                }
                setEndDate(selectedDate);
              }}
              onBlur={(e) => {
                if (e.target.value && isDateBooked(e.target.value)) {
                  setEndDate('');
                }
              }}
              min={startDate || getMinDate()}
              max={getMaxDate()}
              className={isDateBooked(endDate) ? 'booked-date' : ''}
              style={isDateBooked(endDate) ? { backgroundColor: '#e2e8f0', color: '#2d3748', cursor: 'not-allowed', fontWeight: '600', opacity: 0.7, textDecoration: 'line-through' } : {}}
            />
            {isDateBooked(endDate) && (
              <span className="date-warning" style={{ fontSize: '13px', color: '#2d3748', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                <span style={{ fontSize: '16px' }}>🚫</span>
                <span style={{ textDecoration: 'line-through' }}>This date is unavailable (booked or blocked)</span>
              </span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Your Name</label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Enter your full name"
            />
          </div>
          
          <div className="form-group">
            <label>Your Email</label>
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="Enter your email"
            />
          </div>
        </div>

        <div className="price-summary">
          <div className="price-breakdown">
            <div className="price-item">
              <span>Price per night:</span>
              <span>{property.price} HBAR</span>
            </div>
            <div className="price-item">
              <span>Nights:</span>
              <span>
                {startDate && endDate 
                  ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))
                  : 0
                }
              </span>
            </div>
            <div className="price-item total">
              <span>Total:</span>
              <span>{calculateTotalPrice()} HBAR</span>
            </div>
          </div>
        <div style={{ marginTop: 10, fontSize: 12, color: '#4a5568' }}>
          <strong>Legend:</strong> <span style={{ background: '#fed7d7', color: '#c53030', padding: '2px 6px', borderRadius: 8 }}>Booked/Blocked</span> dates are unavailable.
        </div>
        </div>

        <button
          className="book-button"
          onClick={handleBooking}
          disabled={isBooking || !startDate || !endDate || !guestEmail || !guestName}
        >
          {isBooking ? 'Processing...' : 'Book Now'}
        </button>
      </div>

      {(bookedDates.length > 0 || blockedDates.length > 0) && (
        <div className="booked-dates-info">
          <h4>Unavailable Dates (Greyed Out & Bold)</h4>
          <div className="booked-dates-list">
            {[...bookedDates, ...blockedDates.filter(d => !bookedDates.includes(d))]
              .sort()
              .slice(0, 20)
              .map((date, index) => (
              <span key={index} className="booked-date-tag">
                {new Date(date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
            ))}
            {(bookedDates.length + blockedDates.length) > 20 && (
              <span className="more-dates">+{(bookedDates.length + blockedDates.length) - 20} more unavailable dates</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSystem;
